import { Typography } from "@mui/joy";
import React, { useEffect, useState, useRef } from "react";
import { ChecklistSubmission } from "../../../models/checklists";
import useApiService from "../../../hooks/useApiService";
import { RouteComponentProps } from "../../../models/RouteComponent";
import { useToast } from "../../../context/ToastContext";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDateTime } from "../../../utils/DateUtils";
import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";
import { Paginator } from "primereact/paginator";
import { useNavigate } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { Badge } from "primereact/badge";
import { Toast } from "primereact/toast";
import ChecklistTemplateViewDialog from "../../checklist/ChecklistTemplateViewDialog";
import { getChecklistTemplate } from "../../../service/checklistService";

interface ChecklistTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  frequency_type: string;
  active: boolean;
  client_id: string | null;
  is_master_template: boolean;
  created_at: string;
  category_count: number;
  question_count: number;
  conditions?: any[];
}

interface SiteInfo {
  id: string;
  name: string;
  industry_id: string;
  industry_name: string;
  employee_count: number;
}

type PaginationType = {
  first: number;
  rows: number;
  page: number;
  totalPages: number;
};

function ChecklistSubmissionsPage(props: RouteComponentProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const [submissions, setSubmissions] = useState<ChecklistSubmission[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>();
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [siteInfo, setSiteInfo] = useState<SiteInfo | null>(null);
  const [viewDialogVisible, setViewDialogVisible] = useState(false);
  const [templateToView, setTemplateToView] = useState<any>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);

  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });

  const { loading, fetchData: getSubmissions } = useApiService<{
    content: ChecklistSubmission[];
    totalRecords: number;
  }>({
    url: "checklist-submission",
    fetchOnMount: false,
    apiName: "core",
    method: "GET",
    includeTotal: true,
    onError: () => {
      toast("error", "Error loading the submissions, please try again later");
    },
    onSuccess: data => {
      setSubmissions(data.content);
      setTotalRecords(data.totalRecords);
    },
  });

  const { loading: templatesLoading, fetchData: getTemplates } = useApiService<{
    templates: ChecklistTemplate[];
    site: SiteInfo;
  }>({
    url: `checklist-templates-site?siteId=${props.session.site_id}`,
    fetchOnMount: false,
    apiName: "core",
    method: "GET",
    onError: error => {
      console.error("Templates API error:", error);
      toast("error", "Error loading checklist templates, please try again later");
    },
    onSuccess: response => {
      console.log("Templates API response:", response);
      console.log("Templates array:", response.templates);
      console.log("Site info:", response.site);

      if (response.templates && response.site) {
        console.log("Setting templates state with:", response.templates);
        console.log("Setting site info state with:", response.site);
        setTemplates(response.templates);
        setSiteInfo(response.site);
        console.log("Templates loaded successfully. Count:", response.templates.length);
      } else {
        console.warn("Templates or site missing in response:", response);
      }
    },
  });

  useEffect(() => {
    async function init() {
      await getSubmissions({
        fetchParams: {
          rows: paginationParams.rows,
          page: paginationParams.page,
        },
      });
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load templates when switching to Templates tab
  useEffect(() => {
    console.log(
      "Templates tab effect triggered. activeTab:",
      activeTab,
      "templates.length:",
      templates.length
    );
    if (activeTab === 1 && templates.length === 0) {
      console.log("Fetching templates...");
      getTemplates({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Debug: Log templates state changes
  useEffect(() => {
    console.log("Templates state changed:", templates);
    console.log("Site info state changed:", siteInfo);
  }, [templates, siteInfo]);

  const handlePagination = async (e: PaginationType) => {
    setPaginationParams({
      first: e.first,
      rows: e.rows,
      page: e.page,
    });

    await getSubmissions({
      fetchParams: {
        rows: e.rows,
        page: e.page,
      },
    });
  };

  function handleRowClick(e: DataTableRowClickEvent) {
    const rowData: ChecklistSubmission = e.data as ChecklistSubmission;
    navigate({
      pathname: `/site-checklist-submissions/${rowData.id}`,
    });
  }

  // Handle template row click to view template details
  async function handleTemplateClick(e: DataTableRowClickEvent) {
    const template: ChecklistTemplate = e.data as ChecklistTemplate;
    console.log("Template clicked:", template);

    setIsViewLoading(true);
    try {
      const templateData = await getChecklistTemplate(template.id);
      console.log("Template details fetched:", templateData);
      setTemplateToView(templateData);
      setViewDialogVisible(true);
    } catch (error) {
      console.error("Error loading template:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load template details",
        life: 3000,
      });
    } finally {
      setIsViewLoading(false);
    }
  }

  // Render methods
  const renderCreatedDate = (row: ChecklistSubmission) => {
    return <span>{formatDateTime(row.created_at)}</span>;
  };

  const renderCompiantStatus = (row: ChecklistSubmission) => {
    return (
      <StatusBadge variant={row.overall_compliance ? "success" : "error"}>
        {row.overall_compliance ? "Compliant" : "Non compliant"}
      </StatusBadge>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Checklists
      </Typography>

      <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
        <TabPanel header="Submissions">
          <DataTable
            value={submissions}
            className={`policies-procedures-table`}
            tableStyle={{ minWidth: "50rem" }}
            loading={loading}
            onRowClick={handleRowClick}
            lazy={true}
            first={paginationParams.first}
            totalRecords={totalRecords}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPage={async page => {
              // @ts-ignore
              await handlePagination(page as PaginationType);
            }}
          >
            <Column header={"Created at"} body={renderCreatedDate} />
            <Column header={"Name"} field={"template_name"} />
            <Column header={"Completed by"} field={"executor_full_name"} />
            <Column header={"Compliant"} body={renderCompiantStatus} />
          </DataTable>
        </TabPanel>

        <TabPanel header="Templates">
          {templatesLoading ? (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
              <p>Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
              <i className="pi pi-list" style={{ fontSize: "3rem", marginBottom: "1rem" }} />
              <p>No checklist templates available for this site.</p>
            </div>
          ) : (
            <DataTable
              value={templates}
              className="policies-procedures-table"
              tableStyle={{ minWidth: "50rem" }}
              loading={templatesLoading || isViewLoading}
              onRowClick={handleTemplateClick}
              selectionMode="single"
              style={{ cursor: "pointer" }}
            >
              <Column field="name" header="Template Name" style={{ fontWeight: 500 }} />
              <Column
                field="description"
                header="Description"
                body={(row: ChecklistTemplate) => row.description || "-"}
              />
              <Column
                field="category_count"
                header="Categories"
                body={(row: ChecklistTemplate) => (
                  <Badge value={row.category_count} severity="info" />
                )}
                style={{ width: "120px", textAlign: "center" }}
              />
              <Column
                field="question_count"
                header="Questions"
                body={(row: ChecklistTemplate) => (
                  <Badge value={row.question_count} severity="info" />
                )}
                style={{ width: "120px", textAlign: "center" }}
              />
              <Column
                field="frequency_type"
                header="Frequency"
                body={(row: ChecklistTemplate) => (
                  <Badge
                    value={row.frequency_type}
                    severity={row.frequency_type === "daily" ? "warning" : "success"}
                  />
                )}
                style={{ width: "120px", textAlign: "center" }}
              />
            </DataTable>
          )}
        </TabPanel>
      </TabView>

      <Toast ref={toastRef} />

      <ChecklistTemplateViewDialog
        visible={viewDialogVisible}
        onHide={() => {
          setViewDialogVisible(false);
          setTemplateToView(null);
        }}
        template={templateToView}
      />
    </>
  );
}

export default ChecklistSubmissionsPage;
